import React, { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { formatApiErrorDetail } from "../lib/api";
import { useLocalization } from "../hooks/useLocalization";

/** Shared structured-reason dialog - replaces window.prompt()/window.confirm() everywhere a
 * decision requires a documented reason (rejections, cancellations, waivers, force-overrides).
 * Always shows the relevant context (who/what/current state) above the reason field so the
 * decision-maker never has to remember details from another screen, and always preserves the
 * reason in the audit trail via the caller's onConfirm handler. Errors are caught here (toasted,
 * dialog stays open) so every caller gets consistent behavior without repeating try/catch. */
export function ReasonDialog({
  open, onOpenChange, title, description, contextItems = [], reasonLabel,
  reasonPlaceholder, confirmLabel, cancelLabel,
  danger = false, onConfirm, testId, required = true,
}) {
  const { t } = useLocalization();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (required && !reason.trim()) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      setReason("");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("common.somethingWentWrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { onOpenChange(v); if (!v) setReason(""); } }}>
      <DialogContent data-testid={testId} className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle data-testid={`${testId}-title`}>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {contextItems.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1.5 text-sm" data-testid={`${testId}-context`}>
            {contextItems.map((item, i) => (
              <div key={i} className="flex justify-between gap-3">
                <span className="text-gray-500">{item.label}</span>
                <span className="font-medium text-gray-800 text-right">{item.value}</span>
              </div>
            ))}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor={`${testId}-reason`}>{reasonLabel || t("common.reason")} {required && <span className="text-red-500">*</span>}</Label>
          <Textarea
            id={`${testId}-reason`} data-testid={`${testId}-reason-input`} value={reason}
            onChange={(e) => setReason(e.target.value)} placeholder={reasonPlaceholder || t("common.explainWhy")} rows={3} autoFocus
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" data-testid={`${testId}-cancel-btn`} onClick={() => onOpenChange(false)} disabled={busy}>
            {cancelLabel || t("common.cancel")}
          </Button>
          <Button type="button" variant={danger ? "destructive" : "default"} data-testid={`${testId}-confirm-btn`}
            onClick={handleConfirm} disabled={busy || (required && !reason.trim())}>
            {confirmLabel || t("common.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
